import { PENDING_ACTIONS } from "@/domains/profile/models/constants";
import { PROGRESS_RETURNING_PATIENT_FIXTURE } from "@/domains/progress/models/progress-fixtures";
import { createInitialCareplan, createInitialCareplanMeta } from "@/mocks/data/careplans";
import { createInitialMessages, getThreadId } from "@/mocks/data/messages";
import { createInitialProfile } from "@/mocks/data/profiles";
import { PERSONA_ID, type PersonaId } from "@/mocks/personas";
import type {
  CareplanMetaWire,
  CareplansWire,
  MessageWire,
  PendingActionWire,
  ProfileWire,
  ProgressWire,
} from "@/mocks/types";

export type PersonaState = {
  profile: ProfileWire;
  careplan: CareplansWire | null;
  careplanMeta: CareplanMetaWire;
  threadId: number;
  messages: MessageWire[];
  pendingActions: PendingActionWire[];
  progress: ProgressWire | null;
};

export type MockCartLine = {
  id: string;
  variantId: string;
  quantity: number;
  sellingPlanId: string | null;
};

export type MockCart = {
  id: string;
  token: string;
  lines: MockCartLine[];
  discountCodes: string[];
  attributes: Array<{ key: string; value: string }>;
};

const createInitialPersonaState = (personaId: PersonaId): PersonaState => {
  const isSubscribed = personaId === PERSONA_ID.SUBSCRIBED;

  return {
    profile: createInitialProfile(personaId),
    careplan: createInitialCareplan(personaId),
    careplanMeta: createInitialCareplanMeta(personaId),
    threadId: getThreadId(personaId),
    messages: createInitialMessages(personaId),
    pendingActions: isSubscribed ? [{ action: PENDING_ACTIONS.COMPLETE_SURVEY }] : [],
    progress: isSubscribed ? structuredClone(PROGRESS_RETURNING_PATIENT_FIXTURE) : null,
  };
};

const personaStates = new Map<PersonaId, PersonaState>();

export const mockCarts = new Map<string, MockCart>();

export const getPersonaState = (personaId: PersonaId) => {
  const existingState = personaStates.get(personaId);

  if (existingState) {
    return existingState;
  }

  const initialState = createInitialPersonaState(personaId);
  personaStates.set(personaId, initialState);

  return initialState;
};

export const resetMockState = () => {
  personaStates.clear();
  mockCarts.clear();
};
